import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Stack,
  Chip,
  Select,
  MenuItem,
  IconButton,
  Button,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  RefreshCw,
  Settings,
  Server,
} from 'lucide-react';

export const Navbar = ({
  overview = {},
  username = '',
  vhost = '/',
  refreshInterval = 5,
  onRefreshChange = () => {},
  isRefreshing = false,
  onManualRefresh = () => {},
  onOpenSettings = () => {},
}) => {
  return (
    <AppBar
      position="relative"
      color="inherit"
      elevation={0}
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: '#ffffff',
      }}
    >
      <Toolbar
        sx={{
          justifyContent: 'space-between',
          width: '100%',
          px: { xs: 2, sm: 3, md: 4 },
          minHeight: '64px',
        }}
      >
        {/* Left: Brand & Cluster Details */}
        <Stack direction="row" alignItems="center" spacing={2.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #006951 0%, #008f6e 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                boxShadow: '0 4px 10px rgba(0, 105, 81, 0.25)',
              }}
            >
              <svg
                viewBox="0 0 24 24"
                style={{ width: 20, height: 20, fill: 'currentColor' }}
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M4 10.5C4 9.12 5.12 8 6.5 8C7.16 8 7.76 8.26 8.21 8.68C8.98 7.07 10.63 6 12.5 6C14.73 6 16.63 7.53 17.15 9.61C17.7 9.23 18.37 9 19.1 9C20.7 9 22 10.3 22 11.9C22 12.44 21.85 12.95 21.6 13.38C21.85 13.88 22 14.43 22 15C22 17.21 20.21 19 18 19H7C4.79 19 3 17.21 3 15C3 14.33 3.16 13.7 3.44 13.14C3.16 12.39 3 11.58 3 10.74C3 10.66 3 10.58 3.01 10.5H4Z" />
              </svg>
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                  Rabbit<Box component="span" sx={{ color: '#006951' }}>MQ</Box>
                </Typography>
                <Chip
                  label="Dashboard"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    bgcolor: 'rgba(0, 105, 81, 0.1)',
                    color: '#006951',
                    borderRadius: 1,
                  }}
                />
              </Stack>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.2 }}>
                RabbitMQ {overview.rabbitmq_version ? `v${overview.rabbitmq_version}` : 'Cluster'}
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              height: 24,
              width: '1px',
              bgcolor: 'divider',
            }}
          />

          {/* Cluster Status Pill */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: 5,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: '#f1f5f9',
              fontSize: '0.75rem',
            }}
          >
            <Server style={{ width: 14, height: 14, color: '#006951' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Cluster:
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {overview.cluster_name || 'Active Broker'}
            </Typography>
            {overview.is_simulated ? (
              <Chip
                label="Demo Mode"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  bgcolor: 'rgba(245, 158, 11, 0.15)',
                  color: '#d97706',
                }}
              />
            ) : (
              <Chip
                label="Live"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  bgcolor: 'rgba(16, 185, 129, 0.15)',
                  color: '#059669',
                }}
              />
            )}
          </Box>
        </Stack>

        {/* Right: Controls & User Profile */}
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {/* Refresh Controls */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: '#fff',
            }}
          >
            <Tooltip title="Click to refresh now">
              <IconButton
                size="small"
                onClick={onManualRefresh}
                sx={{
                  p: 0.5,
                  color: '#006951',
                  animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              >
                <RefreshCw style={{ width: 15, height: 15 }} />
              </IconButton>
            </Tooltip>

            <Select
              size="small"
              value={refreshInterval}
              onChange={(e) => onRefreshChange(Number(e.target.value))}
              variant="standard"
              disableUnderline
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'text.primary',
                '& .MuiSelect-select': { py: 0.2, pr: '20px !important' },
              }}
            >
              <MenuItem value={2} sx={{ fontSize: '0.8rem' }}>Refresh every 2s</MenuItem>
              <MenuItem value={5} sx={{ fontSize: '0.8rem' }}>Refresh every 5s</MenuItem>
              <MenuItem value={10} sx={{ fontSize: '0.8rem' }}>Refresh every 10s</MenuItem>
              <MenuItem value={30} sx={{ fontSize: '0.8rem' }}>Refresh every 30s</MenuItem>
              <MenuItem value={0} sx={{ fontSize: '0.8rem' }}>Paused</MenuItem>
            </Select>
          </Box>

          {/* Settings Button */}
          <Button
            size="small"
            variant="outlined"
            onClick={onOpenSettings}
            startIcon={<Settings style={{ width: 14, height: 14 }} />}
            sx={{
              color: 'text.primary',
              borderColor: 'divider',
              fontSize: '0.75rem',
              fontWeight: 600,
              py: 0.6,
              px: 1.5,
              '&:hover': {
                borderColor: '#006951',
                color: '#006951',
                bgcolor: 'rgba(0, 105, 81, 0.04)',
              },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Settings</Box>
          </Button>

          {/* User Profile */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              pl: 1,
              borderLeft: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Avatar
              sx={{
                width: 30,
                height: 30,
                fontSize: '0.75rem',
                fontWeight: 700,
                bgcolor: 'rgba(0, 105, 81, 0.1)',
                color: '#006951',
              }}
            >
              {username ? username.slice(0, 2).toUpperCase() : 'MQ'}
            </Avatar>
            <Box sx={{ lineHeight: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                {username || 'Connected'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                vhost: {decodeURIComponent(vhost || '/')}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};
